import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ParticleCanvas } from '@/components/login/ParticleCanvas';
import { useAuthStore } from '@/stores/auth-store';
import { setLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n';
import type { TenantKey } from '@/services/auth-api';
import '@/styles/login.css';

const LANG_KEYS: Record<SupportedLocale, string> = {
  zh: 'login.lang_zh',
  'zh-TW': 'login.lang_zh_TW',
  en: 'login.lang_en',
  vi: 'login.lang_vi',
  km: 'login.lang_km',
};

const TENANT_OPTIONS: ReadonlyArray<{ key: TenantKey; codeKey: string }> = [
  { key: 'WITTY', codeKey: 'tenant.witty_code' },
  { key: 'PSP', codeKey: 'tenant.psp_code' },
  { key: 'YPC', codeKey: 'tenant.ypc_code' },
];

export function LoginRoute() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const signIn = useAuthStore((s) => s.signIn);

  const [tenant, setTenant] = useState<TenantKey>('PSP');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!username || !password) {
      setError(t('login.validation_required'));
      return;
    }
    setSubmitting(true);
    try {
      await signIn(username, password, tenant);
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setError(status === 401 ? t('login.error_invalid') : t('login.error_network'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <ParticleCanvas />
      <div className="login-bg-gradient" />

      <button
        type="button"
        className="login-dark-toggle"
        onClick={() => setDark((d) => !d)}
        aria-label="Toggle dark mode"
      >
        <span className="material-symbols-outlined">{dark ? 'dark_mode' : 'light_mode'}</span>
      </button>

      <form className="login-card" onSubmit={onSubmit}>
        <div className="login-logo-area">
          <div className="login-logo-mark">
            <span className="material-symbols-outlined">print</span>
          </div>
          <div className="login-logo-text">
            <div className="login-logo-name">
              {t('common.app_name')}<span> {t('common.app_suffix')}</span>
            </div>
            <div className="login-logo-tagline">{t('login.tagline')}</div>
          </div>
        </div>

        <div className="login-form">
          <div className="login-input-wrap">
            <span className="material-symbols-outlined login-input-icon">person</span>
            <input
              className="login-input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              placeholder={t('login.username_placeholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              aria-invalid={Boolean(error) || undefined}
            />
          </div>
          <div className="login-input-wrap">
            <span className="material-symbols-outlined login-input-icon">lock</span>
            <input
              className="login-input"
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('login.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={Boolean(error) || undefined}
            />
            <button
              type="button"
              className="login-pw-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              <span className="material-symbols-outlined">
                {showPw ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
        </div>

        <div className="login-error" role="alert">{error}</div>

        <div className="login-tenant" role="radiogroup" aria-label="Tenant">
          {TENANT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="radio"
              aria-checked={tenant === opt.key}
              className={`login-tenant-btn${tenant === opt.key ? ' active' : ''}`}
              onClick={() => setTenant(opt.key)}
            >
              <span>{opt.key}</span>
              <span className="login-tenant-code">{t(opt.codeKey)}</span>
            </button>
          ))}
        </div>

        <div className="login-lang">
          {SUPPORTED_LOCALES.map((lang) => (
            <button
              key={lang}
              type="button"
              className={`login-lang-btn${i18n.language === lang ? ' active' : ''}`}
              onClick={() => setLocale(lang)}
            >
              {t(LANG_KEYS[lang])}
            </button>
          ))}
        </div>

        <div className="login-divider" />

        <button type="submit" className="login-submit" disabled={submitting}>
          {submitting ? <span className="login-spinner" /> : <span>{t('login.submit')}</span>}
        </button>

        <a href="#" className="login-forgot">{t('login.forgot')}</a>
      </form>

      <div className="login-version">{t('login.version')}</div>
    </main>
  );
}
